import type { Pot, PotSize } from "@/types";
import { POT_SIZE_ORDER, formatApproxVolumeLiters, potSizeShortLabel } from "@/app/lib/potSizes";
import { PlusIcon, PotIcon } from "@/app/components/system/Icons";
import { PotIllustration } from "@/app/components/PotIllustration";
import { SunHoursPicker } from "@/app/components/SunHoursPicker";

export interface PotPickerForm {
  potChoice: "new" | "existing";
  potId: string;
  newPotName: string;
  newPotShape: string;
  newPotSize: PotSize;
  newPotSunStart: string;
  newPotSunEnd: string;
}

interface PotPickerProps {
  pots: Pot[];
  form: PotPickerForm;
  setForm: (next: PotPickerForm) => void;
  /** When set, the pot is fixed (e.g. adding from a pot's screen): show it read-only instead of the picker. */
  lockedPot?: Pot | null;
}

const SHAPES = ["terracotta", "basket", "concrete", "long"] as const;

export function PotPicker({ pots, form, setForm, lockedPot }: PotPickerProps) {
  if (lockedPot) {
    return (
      <div className="mb-3">
        <span className="block mb-1.5 text-xs uppercase tracking-[0.06em] text-ink-3 font-medium">Pot</span>
        <div className="bg-paper border border-line rounded-xl p-2.5 flex items-center gap-3">
          <PotIllustration shape={lockedPot.shape} plants={lockedPot.plants} size="small" />
          <div className="min-w-0">
            <p className="font-sans text-sm font-medium text-ink m-0 truncate">{lockedPot.name}</p>
            <p className="text-xs text-ink-3 m-0">{potSizeShortLabel(lockedPot.size)}</p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="mb-3">
      <span className="block mb-1.5 text-xs uppercase tracking-[0.06em] text-ink-3 font-medium">Pot</span>
      <div className="flex gap-1 bg-paper border border-line p-0.75 rounded-[10px] mb-2.5">
        {(["new", "existing"] as const).map((choice) => {
          const on = form.potChoice === choice;
          const disabled = choice === "existing" && (!pots || pots.length === 0);
          return (
            <button
              key={choice}
              type="button"
              onClick={() => setForm({ ...form, potChoice: choice })}
              disabled={disabled}
              className={`flex-1 border-0 p-2 rounded-[7px] cursor-pointer inline-flex items-center justify-center gap-1.25 font-sans text-[12.5px] font-medium ${on ? "bg-ink text-paper" : "bg-transparent text-ink-3"} disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {choice === "new" ? <PlusIcon size={12} /> : <PotIcon size={12} />}
              {choice === "new" ? "New pot" : "Existing pot"}
            </button>
          );
        })}
      </div>
      {form.potChoice === "existing" && pots && pots.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {pots.map((pot) => {
            const on = form.potId === pot.id;
            return (
              <button
                type="button"
                key={pot.id}
                onClick={() => setForm({ ...form, potId: pot.id })}
                className={`bg-paper border-[1.5px] rounded-xl p-2.5 cursor-pointer flex flex-col items-center gap-1 transition-all duration-150 ${on ? "border-moss bg-moss-soft" : "border-line"}`}
              >
                <PotIllustration shape={pot.shape} plants={[]} />
                <span className="font-sans text-xs font-medium text-ink">{pot.name}</span>
              </button>
            );
          })}
        </div>
      )}
      {form.potChoice === "new" && (
        <div className="flex flex-col gap-2">
          <input
            className="field-input-bare"
            placeholder="Pot name (e.g. Kitchen herb trio)"
            value={form.newPotName}
            onChange={(e) => setForm({ ...form, newPotName: e.target.value })}
          />
          <div className="flex gap-1.5 overflow-x-auto scrollbar-none [&::-webkit-scrollbar]:hidden">
            {SHAPES.map((s) => {
              const on = form.newPotShape === s;
              return (
                <button
                  type="button"
                  key={s}
                  onClick={() => setForm({ ...form, newPotShape: s })}
                  className={`bg-paper border-[1.5px] rounded-[10px] pt-1.5 pb-2 px-1 cursor-pointer flex flex-col items-center gap-0.5 min-w-18 flex-none ${on ? "border-moss bg-moss-soft" : "border-line"}`}
                >
                  <PotIllustration shape={s} plants={[]} />
                  <span className={`text-[10px] capitalize ${on ? "text-moss" : "text-ink-3"}`}>{s}</span>
                </button>
              );
            })}
          </div>
          <span className="text-xs uppercase tracking-[0.06em] text-ink-3 font-medium mt-1">Pot size</span>
          <div className="flex gap-1.5 overflow-x-auto scrollbar-none [&::-webkit-scrollbar]:hidden">
            {POT_SIZE_ORDER.map((sz) => {
              const on = form.newPotSize === sz;
              return (
                <button
                  type="button"
                  key={sz}
                  onClick={() => setForm({ ...form, newPotSize: sz })}
                  className={`bg-paper border-[1.5px] rounded-[10px] py-2 px-2 cursor-pointer flex flex-col items-center gap-0.5 min-w-22 flex-none ${on ? "border-moss bg-moss-soft" : "border-line"}`}
                >
                  <span className={`text-[11px] font-medium capitalize ${on ? "text-moss" : "text-ink"}`}>{sz}</span>
                  <span className={`text-[10px] ${on ? "text-moss" : "text-ink-3"}`}>
                    {formatApproxVolumeLiters(sz)}
                  </span>
                </button>
              );
            })}
          </div>
          <SunHoursPicker
            start={form.newPotSunStart}
            end={form.newPotSunEnd}
            onStartChange={(v) => setForm({ ...form, newPotSunStart: v })}
            onEndChange={(v) => setForm({ ...form, newPotSunEnd: v })}
          />
        </div>
      )}
    </div>
  );
}
